/**
 * Estado do editor "Variáveis do contrato" + "Fórmula de cálculo" (`2j`).
 *
 * Separado do componente porque um `.tsx` que exporta funções quebra o fast
 * refresh (`react-refresh/only-export-components`), e porque estas transições são
 * regra — bloquear a remoção de uma variável em uso — e merecem ser puras.
 *
 * Cada linha guarda o que está SALVO (`salva`, o que entra no contrato) e o que
 * está sendo DIGITADO (`rascunho`). O botão `salvar` da linha é o que promove um
 * no outro. Enviar o formulário com rascunho pendente é bloqueado: senão a pessoa
 * editaria a PTAX, esqueceria o `salvar` da linha e gravaria o valor antigo sem
 * perceber.
 */
import type { VariavelDeContrato } from '@/api/contracts/contrato'

import {
  inserirVariavel,
  removerSegmento,
  segmentar,
  serializar,
  validarSegmentos,
  validarVariaveis,
  validarVariavel,
  type SegmentoDeFormula,
} from './formula'

export type RascunhoDeVariavel = { tag: string; nome: string; descricao: string; valor: string }

export type LinhaDeVariavel = {
  chave: string
  /** `null` = linha nova, ainda não salva. */
  salva: VariavelDeContrato | null
  rascunho: RascunhoDeVariavel
  erro: string | null
}

export type EstadoDeParametros = {
  linhas: LinhaDeVariavel[]
  segmentos: SegmentoDeFormula[]
}

let proximaChave = 0
const novaChave = () => `variavel-${++proximaChave}`

function paraRascunho(variavel: VariavelDeContrato): RascunhoDeVariavel {
  return {
    tag: variavel.tag,
    nome: variavel.nome,
    descricao: variavel.descricao,
    valor: variavel.valor === null ? '' : String(variavel.valor),
  }
}

/** `''` = fornecido pelo servidor (`null`). Vírgula decimal aceita. */
function lerRascunho(rascunho: RascunhoDeVariavel): VariavelDeContrato {
  const valor = rascunho.valor.trim()
  return {
    tag: rascunho.tag.trim(),
    nome: rascunho.nome.trim(),
    descricao: rascunho.descricao.trim(),
    valor: valor === '' ? null : Number(valor.replace(',', '.')),
  }
}

export function estadoInicial(
  variaveis: readonly VariavelDeContrato[],
  formula: string,
): EstadoDeParametros {
  return {
    linhas: variaveis.map((v) => ({
      chave: novaChave(),
      salva: v,
      rascunho: paraRascunho(v),
      erro: null,
    })),
    segmentos: segmentar(
      formula,
      variaveis.map((v) => v.tag),
    ),
  }
}

export function variaveisSalvas(estado: EstadoDeParametros): VariavelDeContrato[] {
  return estado.linhas.flatMap((l) => (l.salva ? [l.salva] : []))
}

export function linhaPendente(linha: LinhaDeVariavel): boolean {
  if (!linha.salva) return true
  const r = paraRascunho(linha.salva)
  return (Object.keys(r) as (keyof RascunhoDeVariavel)[]).some(
    (campo) => r[campo] !== linha.rascunho[campo],
  )
}

function usaNaFormula(estado: EstadoDeParametros, tag: string): boolean {
  return estado.segmentos.some((s) => s.tipo === 'variavel' && s.tag === tag)
}

function comLinha(
  estado: EstadoDeParametros,
  chave: string,
  mudar: (linha: LinhaDeVariavel) => LinhaDeVariavel,
): EstadoDeParametros {
  return { ...estado, linhas: estado.linhas.map((l) => (l.chave === chave ? mudar(l) : l)) }
}

export function adicionarLinha(estado: EstadoDeParametros): EstadoDeParametros {
  return {
    ...estado,
    linhas: [
      ...estado.linhas,
      {
        chave: novaChave(),
        salva: null,
        rascunho: { tag: '', nome: '', descricao: '', valor: '' },
        erro: null,
      },
    ],
  }
}

export function alterarRascunho(
  estado: EstadoDeParametros,
  chave: string,
  campo: keyof RascunhoDeVariavel,
  valor: string,
): EstadoDeParametros {
  return comLinha(estado, chave, (l) => ({
    ...l,
    rascunho: { ...l.rascunho, [campo]: valor },
    erro: null,
  }))
}

export function salvarLinha(estado: EstadoDeParametros, chave: string): EstadoDeParametros {
  const linha = estado.linhas.find((l) => l.chave === chave)
  if (!linha) return estado

  const variavel = lerRascunho(linha.rascunho)
  const outras = estado.linhas.filter((l) => l.chave !== chave && l.salva).map((l) => l.salva!.tag)

  let erro = validarVariavel(variavel, outras)
  if (
    !erro &&
    linha.salva &&
    linha.salva.tag !== variavel.tag &&
    usaNaFormula(estado, linha.salva.tag)
  ) {
    erro = `A variável "${linha.salva.tag}" está na fórmula — remova-a da fórmula antes de trocar a tag.`
  }
  if (erro) return comLinha(estado, chave, (l) => ({ ...l, erro }))

  return comLinha(estado, chave, () => ({
    chave,
    salva: variavel,
    rascunho: paraRascunho(variavel),
    erro: null,
  }))
}

/** 🔒 Bloqueia remover uma variável que a fórmula usa — com a mensagem na linha. */
export function removerLinha(estado: EstadoDeParametros, chave: string): EstadoDeParametros {
  const linha = estado.linhas.find((l) => l.chave === chave)
  if (!linha) return estado
  if (linha.salva && usaNaFormula(estado, linha.salva.tag)) {
    return comLinha(estado, chave, (l) => ({
      ...l,
      erro: `A variável "${linha.salva!.tag}" está na fórmula — remova-a da fórmula antes de excluí-la.`,
    }))
  }
  return { ...estado, linhas: estado.linhas.filter((l) => l.chave !== chave) }
}

export function adicionarAFormula(
  estado: EstadoDeParametros,
  chave: string,
  indiceTexto?: number,
  cursor?: number,
): EstadoDeParametros {
  const linha = estado.linhas.find((l) => l.chave === chave)
  if (!linha?.salva) return estado
  return {
    ...estado,
    segmentos: inserirVariavel(estado.segmentos, linha.salva.tag, indiceTexto, cursor),
  }
}

export function removerDaFormula(estado: EstadoDeParametros, indice: number): EstadoDeParametros {
  // Remover o chip pode destravar a remoção da linha: limpa os avisos.
  return {
    linhas: estado.linhas.map((l) => (l.erro ? { ...l, erro: null } : l)),
    segmentos: removerSegmento(estado.segmentos, indice),
  }
}

export function alterarTexto(
  estado: EstadoDeParametros,
  indice: number,
  valor: string,
): EstadoDeParametros {
  return {
    ...estado,
    segmentos: estado.segmentos.map((s, i) =>
      i === indice && s.tipo === 'texto' ? { tipo: 'texto', valor } : s,
    ),
  }
}

export type ParametrosResolvidos =
  | { ok: true; variaveis: VariavelDeContrato[]; formulaCalculo: string }
  | { ok: false; mensagem: string }

/** O que o `submit` envia — ou por que não pode enviar. */
export function resolverParametros(estado: EstadoDeParametros): ParametrosResolvidos {
  const pendentes = estado.linhas.filter(linhaPendente)
  if (pendentes.length > 0) {
    const nomes = pendentes.map((l) => l.salva?.tag ?? (l.rascunho.tag || 'nova variável'))
    return {
      ok: false,
      mensagem: `Há variáveis com alterações não salvas (${nomes.join(', ')}) — clique em "salvar" na linha ou remova-a.`,
    }
  }
  const variaveis = variaveisSalvas(estado)
  const erroDeVariavel = validarVariaveis(variaveis)
  if (erroDeVariavel) return { ok: false, mensagem: erroDeVariavel }

  const resultado = validarSegmentos(
    estado.segmentos,
    variaveis.map((v) => v.tag),
  )
  if (!resultado.ok) return { ok: false, mensagem: resultado.mensagem }

  return { ok: true, variaveis, formulaCalculo: serializar(estado.segmentos) }
}
